import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Buildings,
  ChatText,
  Envelope,
  FlagBanner,
  Globe,
  IdentificationBadge,
  Lock,
  MapPin,
  Phone,
  User,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { AuthBrandPanel } from '../components/layout/AuthBrandPanel';
import { portalRoutes } from '../lib/portalRoutes';
import { getPortalDesignBootstrap } from '../lib/portalDesignBootstrap';

const fieldLabelClassName = 'font-mono text-[11px] text-muted-foreground';

const IconInput = ({ icon: Icon, ...props }) => (
  <div className="relative">
    <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
    <Input className="pl-10" {...props} />
  </div>
);

export default function Register() {
  const bootstrap = window.portalReactBootstrap?.register ?? {};
  const portalDesign = getPortalDesignBootstrap();
  const formName = typeof bootstrap.formName === 'string' && bootstrap.formName.trim() !== ''
    ? bootstrap.formName
    : 'portal_registration_form';
  const values = bootstrap.values && typeof bootstrap.values === 'object' ? bootstrap.values : {};
  const errors = Array.isArray(bootstrap.errors) ? bootstrap.errors : [];

  const fieldName = (name) => `${formName}[${name}]`;

  return (
    <div className="grid min-h-screen bg-background text-foreground lg:grid-cols-[minmax(560px,760px)_1fr]">
      <div className="flex flex-col overflow-y-auto px-6 py-10 sm:px-10 lg:border-r lg:border-border lg:px-16 lg:py-12">
        <Link to={portalRoutes.login} className="mb-9 inline-flex self-start">
          {portalDesign.logoUrl ? (
            <img
              src={portalDesign.logoUrl}
              alt={portalDesign.portalName}
              className="h-10 w-auto max-w-[220px] object-contain"
            />
          ) : (
            <span className="font-heading text-2xl font-extrabold">{portalDesign.portalName}</span>
          )}
        </Link>

        <div className="mb-1 flex items-center gap-3">
          <div className="h-10 w-2.5 shrink-0 -skew-x-12 bg-primary" />
          <h1 className="font-heading text-4xl font-black leading-none">
            Händler <span className="text-primary">werden</span>
          </h1>
        </div>
        <p className="mb-7 mt-3 max-w-lg text-base leading-relaxed text-muted-foreground">
          Registriere deinen Betrieb und erhalte Zugang zu Files, Preisliste und Support.
        </p>

        <div className="mb-6 flex max-w-md gap-1.5 rounded-md border border-border bg-secondary p-1.5">
          <Link
            to={portalRoutes.login}
            className="flex-1 rounded-sm py-2.5 text-center font-heading text-sm font-bold text-muted-foreground no-underline transition-colors hover:text-foreground"
          >
            Anmelden
          </Link>
          <span className="flex-1 rounded-sm bg-primary py-2.5 text-center font-heading text-sm font-bold text-primary-foreground">
            Registrieren
          </span>
        </div>

        <form method="post" action={portalRoutes.register} className="max-w-2xl space-y-5">
          <input type="hidden" name={fieldName('_token')} value={bootstrap.csrfToken || ''} />

          {errors.length > 0 ? (
            <div
              className="rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              data-testid="register-errors"
            >
              <ul className="space-y-1">
                {errors.map((error, index) => (
                  <li key={`${error}-${index}`}>{error}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="vorname" className={fieldLabelClassName}>Vorname</Label>
              <IconInput
                icon={User}
                id="vorname"
                name={fieldName('vorname')}
                defaultValue={values.vorname || ''}
                placeholder="Max"
                data-testid="register-firstname"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nachname" className={fieldLabelClassName}>Nachname</Label>
              <IconInput
                icon={User}
                id="nachname"
                name={fieldName('nachname')}
                defaultValue={values.nachname || ''}
                placeholder="Mustermann"
                data-testid="register-lastname"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="firma" className={fieldLabelClassName}>Firma / Werkstatt</Label>
            <IconInput
              icon={Buildings}
              id="firma"
              name={fieldName('firmenname')}
              defaultValue={values.firmenname || ''}
              placeholder="Mustermann Performance GmbH"
              data-testid="register-company"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="mail" className={fieldLabelClassName}>E-Mail</Label>
              <IconInput
                icon={Envelope}
                id="mail"
                name={fieldName('email')}
                type="email"
                defaultValue={values.email || ''}
                placeholder="name@werkstatt.de"
                autoComplete="email"
                data-testid="register-email"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="telefon" className={fieldLabelClassName}>Telefon</Label>
              <IconInput
                icon={Phone}
                id="telefon"
                name={fieldName('telefon')}
                defaultValue={values.telefon || ''}
                placeholder="+49 ..."
                autoComplete="tel"
                data-testid="register-phone"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="adresse" className={fieldLabelClassName}>Adresse</Label>
            <IconInput
              icon={MapPin}
              id="adresse"
              name={fieldName('adresse')}
              defaultValue={values.adresse || ''}
              placeholder="Straße, Hausnummer, PLZ Ort"
              data-testid="register-address"
              required
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="website" className={fieldLabelClassName}>Web-Adresse</Label>
              <IconInput
                icon={Globe}
                id="website"
                name={fieldName('website')}
                defaultValue={values.website || ''}
                placeholder="https://..."
                autoComplete="url"
                data-testid="register-website"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ust-id" className={fieldLabelClassName}>USt-Id</Label>
              <IconInput
                icon={IdentificationBadge}
                id="ust-id"
                name={fieldName('ustId')}
                defaultValue={values.ustId || ''}
                data-testid="register-ust-id"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="land" className={fieldLabelClassName}>Land</Label>
            <IconInput
              icon={FlagBanner}
              id="land"
              name={fieldName('land')}
              defaultValue={values.land || 'Deutschland'}
              data-testid="register-country"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="firmenbeschreibung" className={fieldLabelClassName}>Kurzbeschreibung</Label>
            <IconInput
              icon={ChatText}
              id="firmenbeschreibung"
              name={fieldName('firmenbeschreibung')}
              defaultValue={values.firmenbeschreibung || ''}
              placeholder="Was macht eure Firma?"
              data-testid="register-company-description"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="kennwort" className={fieldLabelClassName}>Passwort</Label>
              <IconInput
                icon={Lock}
                id="kennwort"
                name={fieldName('plainPassword[first]')}
                type="password"
                defaultValue={values.plainPasswordFirst || ''}
                placeholder="••••••••"
                autoComplete="new-password"
                minLength={8}
                data-testid="register-password"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="kennwort2" className={fieldLabelClassName}>Passwort bestätigen</Label>
              <IconInput
                icon={Lock}
                id="kennwort2"
                name={fieldName('plainPassword[second]')}
                type="password"
                defaultValue={values.plainPasswordSecond || ''}
                placeholder="••••••••"
                autoComplete="new-password"
                minLength={8}
                data-testid="register-password-repeat"
                required
              />
            </div>
          </div>

          <label className="flex items-start gap-3 rounded-sm border border-border bg-secondary/40 px-3 py-2.5 text-sm text-muted-foreground">
            <Checkbox id="newsletter" name={fieldName('newsletter')} value="1" defaultChecked={Boolean(values.newsletter)} className="mt-0.5" />
            Ich möchte Updates und relevante Informationen erhalten.
          </label>

          <Button className="btn-gradient h-11 w-full text-white" type="submit" data-testid="register-submit">
            Registrierung starten
            <ArrowRight className="ml-1 h-4 w-4" weight="bold" />
          </Button>
        </form>

        <div className="mt-6 max-w-2xl text-sm text-muted-foreground">
          Bereits registriert?{' '}
          <Link to={portalRoutes.login} className="text-primary hover:underline">
            Zum Login
          </Link>
        </div>
      </div>

      <AuthBrandPanel portalName={portalDesign.portalName} />
    </div>
  );
}
